import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { Logout, Settings } from '@mui/icons-material';
import {
  Button,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  logoutSSOUser,
  logoutUser,
  resetUser,
  selectUser,
} from 'state/userSlice';
import {
  resetWorkflowState,
  selectCurrentProjectInfo,
} from 'state/workflowSlice';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { showToast } from 'features/common/toasts/ToastProvider';

import styles from './UserDropdown.module.css';

export function UserDropdown() {
  const dispatch = useAppDispatch();
  const userProject = useAppSelector(selectCurrentProjectInfo);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { sso_username, email, first_name, last_name, insight_url } =
    useAppSelector(selectUser);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const logOut = useCallback(() => {
    if (sso_username) {
      void (async () => {
        try {
          await dispatch(logoutSSOUser()).unwrap();
        } catch {
          showToast('error', 'Sign out failed, try again.');
        }
      })();
    } else {
      dispatch(resetWorkflowState());
      dispatch(resetUser());
      dispatch(logoutUser());
    }
  }, [dispatch, sso_username]);

  const handleLogout = () => {
    handleMenuClose();
    logOut();
  };

  const handleUserSettings = () => {
    handleMenuClose();
    const orgId = userProject?.org_id ?? '';
    const returnUrl = `${window.location.origin}/server/users/settings-return/`;
    const userSettingsUrl = `${insight_url}/usersettings?return_to=${returnUrl}&org_id=${orgId}`;
    window.location.href = userSettingsUrl;
  };

  useEffect(() => () => handleMenuClose(), []);

  const title = useMemo(() => {
    const t = `${first_name} ${last_name}`.trim();
    return t || 'User';
  }, [first_name, last_name]);

  const menuId = 'user-menu';

  return (
    <>
      <Button
        onClick={handleMenuOpen}
        startIcon={<FontAwesomeIcon name="user" />}
        className={styles.userButton}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? menuId : undefined}
      >
        <Typography variant="body2">User</Typography>
      </Button>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{ className: styles.menuPaper }}
        MenuListProps={{ className: styles.menuList }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Stack className={styles.userInfo}>
          <Typography variant="body1" className={styles.username}>
            {title}
          </Typography>
          <Typography variant="body2" className={styles.email}>
            {email || ''}
          </Typography>
        </Stack>
        <Divider />
        {sso_username && (
          <MenuItem className={styles.logoutItem} onClick={handleUserSettings}>
            <ListItemIcon className={styles.logoutIcon}>
              <Settings fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2" className={styles.logoutText}>
              User Settings
            </Typography>
          </MenuItem>
        )}
        <MenuItem className={styles.logoutItem} onClick={handleLogout}>
          <ListItemIcon className={styles.logoutIcon}>
            <Logout fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" className={styles.logoutText}>
            Sign Out
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
